import React, { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import AlertModal from '../components/AlertModal';
import useAlert from '../hooks/useAlert';
import { event } from '../analytics';
import { useAuditLog } from '../hooks/useAuditLog';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material';
import {
  Add,
  PlayArrow,
  Stop,
  Edit,
  Delete,
  ExpandMore,
  Code,
  Assessment,
  BugReport,
} from '@mui/icons-material';
import {
  SEQUENCES_QUERY,
  CREATE_SEQUENCE_MUTATION,
  CREATE_SEQUENCE_FROM_TEMPLATE_MUTATION,
  UPDATE_SEQUENCE_MUTATION,
  DELETE_SEQUENCE_MUTATION,
  EXECUTE_SEQUENCE_MUTATION,
  Sequence,
  SequenceTemplate,
} from '../graphql/sequences';
import { GET_CURRENT_USER_WITH_ROLE } from '../graphql/settings';
import { SequenceBuilder } from '../components/SequenceBuilder';

const Sequences: React.FC = () => {
  console.log('🔍 SEQUENCES COMPONENT INITIALIZED');
  
  const { alertState, showSuccess, showError, showInfo, hideAlert } = useAlert();
  const { logEvent } = useAuditLog();
  
  // Check user role for edit permissions
  const { data: meData } = useQuery(GET_CURRENT_USER_WITH_ROLE);
  const canEdit = meData?.me?.isEditor || meData?.me?.isAdmin || false;
  const [selectedSequence, setSelectedSequence] = useState<Sequence | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SequenceTemplate | null>(null);
  const [newSequenceName, setNewSequenceName] = useState('');
  const [templateVariables, setTemplateVariables] = useState('{}');
  const [editingSequence, setEditingSequence] = useState<Sequence | null>(null);
  const [sequenceBuilderOpen, setSequenceBuilderOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sequenceToDelete, setSequenceToDelete] = useState<Sequence | null>(null);
  const [runningSequences, setRunningSequences] = useState<Set<string>>(new Set());
  
  // IMPORTANT: Freeze the editing sequence data when modal opens to prevent re-renders from resetting it
  const frozenEditingSequenceRef = React.useRef<Sequence | null>(null);

  // GraphQL queries - use cache-first to prevent automatic refetching that resets the modal
  const { data: sequencesData, loading: sequencesLoading, refetch: refetchSequences, error: sequencesError } = useQuery(SEQUENCES_QUERY, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: false,
    fetchPolicy: 'cache-first', // Only fetch from network on mount or explicit refetch() - prevents re-renders while editing
  });

  // GraphQL mutations
  const [createSequence] = useMutation(CREATE_SEQUENCE_MUTATION);
  const [createFromTemplate] = useMutation(CREATE_SEQUENCE_FROM_TEMPLATE_MUTATION);
  const [updateSequence] = useMutation(UPDATE_SEQUENCE_MUTATION);
  const [deleteSequence] = useMutation(DELETE_SEQUENCE_MUTATION);
  const [executeSequence] = useMutation(EXECUTE_SEQUENCE_MUTATION);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'success';
      case 'running': return 'secondary';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'paused': return 'default';
      default: return 'default';
    }
  };

  const getBiasScoreColor = (score?: number) => {
    if (!score) return 'default';
    if (score < 0.3) return 'success';
    if (score < 0.7) return 'warning';
    return 'error';
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate || !newSequenceName) return;

    try {
      await createFromTemplate({
        variables: {
          templateId: selectedTemplate.id,
          name: newSequenceName,
          variables: templateVariables
        }
      });
      
      event('sequence_create_from_template', { event_category: 'sequences' });
      setTemplateDialogOpen(false);
      setNewSequenceName('');
      setTemplateVariables('{}');
      setSelectedTemplate(null);
      refetchSequences();
    } catch (error) {
      console.error('Failed to create sequence from template:', error);
    }
  };

  const handleCreateNewSequence = () => {
    frozenEditingSequenceRef.current = null;
    setEditingSequence(null);
    setSequenceBuilderOpen(true);
  };

  const handleEditSequence = async (sequence: Sequence) => {
    console.log('🔍 [FRONTEND] Editing sequence:', sequence.id);
    // IMPORTANT: Freeze the sequence data in a ref so re-renders don't affect it
    frozenEditingSequenceRef.current = sequence;
    setEditingSequence(sequence);
    setSequenceBuilderOpen(true);
  };

  const handleCloseSequenceBuilder = () => {
    setSequenceBuilderOpen(false);
    setEditingSequence(null);
    frozenEditingSequenceRef.current = null;
  };

  const handleDeleteSequence = (sequence: Sequence) => {
    setSequenceToDelete(sequence);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteSequence = async () => {
    if (!sequenceToDelete) return;

    try {
      await deleteSequence({
        variables: { id: sequenceToDelete.id }
      });
      
      event('sequence_delete', { event_category: 'sequences' });
      setDeleteConfirmOpen(false);
      setSequenceToDelete(null);
      refetchSequences();
    } catch (error) {
      console.error('Failed to delete sequence:', error);
      showError('Failed to delete sequence. It may be assigned to API configurations.');
    }
  };

  const handleExecuteSequence = async (sequence: Sequence) => {
    try {
      setRunningSequences(prev => new Set(prev).add(sequence.id));
      
      const { data } = await executeSequence({
        variables: { sequenceId: sequence.id }
      });

      if (data?.executeSequence) {
        const result = data.executeSequence;
        
        if (result.status === 'failed') {
          // Show error message when execution failed
          const errorMsg = result.errorMessage || 'Sequence execution failed';
          showError(`Sequence "${sequence.name}" failed to start: ${errorMsg}`);
        } else {
          // Show success with execution ID when execution succeeded
          event('sequence_execute', { event_category: 'sequences', sequence_id: sequence.id });
          showSuccess(`Sequence "${sequence.name}" started successfully!\n\nExecution ID: ${result.executionId}`);
        }
        refetchSequences(); // Refresh to show updated status
      }
    } catch (error: any) {
      console.error('Failed to execute sequence:', error);
      showError(`Failed to execute sequence: ${error.message}`);
    } finally {
      setRunningSequences(prev => {
        const newSet = new Set(prev);
        newSet.delete(sequence.id);
        return newSet;
      });
    }
  };

  const handleSaveSequence = async (sequenceData: any) => {
    console.log('🔍 [FRONTEND] Starting handleSaveSequence with data:', sequenceData);
    console.log('🔍 [FRONTEND] Steps data:', sequenceData.steps);
    
    try {
      if (editingSequence) {
        console.log('🔄 [FRONTEND] Updating existing sequence:', editingSequence.id);
        
        // Map steps with detailed debugging
        const mappedSteps = sequenceData.steps.map((step: any, index: number) => {
          console.log(`🔍 [FRONTEND] Processing step ${index}:`, step);
          console.log(`🔍 [FRONTEND] Step type: ${step.stepType}, Method: ${step.method}`);
          
          const baseStep = {
            stepNumber: step.stepNumber,
            name: step.name,
            stepType: step.stepType === 'httpRequest' ? 'http_request' : 
                     step.stepType === 'timer' ? 'timer' : 'action',
            analyzeForBias: step.analyzeForBias,
            analysisText: step.analysisText || '',
            variableDefinitions: step.variableDefinitions || [],
            waitSeconds: step.waitSeconds,
            retryOnFailure: step.retryOnFailure,
            skipConditionVariable: step.skipCondition?.variable || '',
            skipConditionOperator: step.skipCondition?.operator || 'equals',
            skipConditionValue: step.skipCondition?.value || '',
            actionBlocks: JSON.stringify(step.actionBlocks || []),
            // RAG Analysis Configuration
            ragAnalysisEnabled: step.ragAnalysisEnabled || false,
            ragQueryVariable: step.ragQueryVariable || '',
            ragContextVariable: step.ragContextVariable || '',
            ragResponseVariable: step.ragResponseVariable || '',
            // File Attachments
            fileAttachments: step.fileAttachments || [],
          };

          // Only add HTTP-specific fields for HTTP request steps
          if (step.stepType === 'httpRequest') {
            const httpStep = {
              ...baseStep,
              url: step.url,
              httpMethod: step.method,
              headers: step.headers,
              requestBody: step.requestBody,
              requestFormat: step.requestBodyType || 'json',
              responseVariableMappings: step.responseMapping,
              analysisTextTemplate: step.analysisTextTemplate,
              analysisIncludeVariables: step.analysisIncludeVariables,
              analysisIncludeResponseData: step.analysisIncludeResponseData,
              analysisIncludeContext: step.analysisIncludeContext
            };
            console.log(`🔍 [FRONTEND] HTTP step ${index} mapped:`, httpStep);
            return httpStep;
          }

          console.log(`🔍 [FRONTEND] Non-HTTP step ${index} mapped:`, baseStep);
          return baseStep;
        });
        
        console.log('🔍 [FRONTEND] All mapped steps:', mappedSteps);
        
        // Update existing sequence
        await updateSequence({
          variables: {
            id: editingSequence.id,
            input: {
              name: sequenceData.name,
              description: sequenceData.description,
              status: sequenceData.status,
              executionIntervalMinutes: 60,
              maxRetries: 3,
              timeoutSeconds: 30,
              globalVariables: JSON.stringify(sequenceData.globalVariables),
              analysisVariables: sequenceData.analysisVariables || [],
              ignoredHarmCategories: sequenceData.ignoredHarmCategories || [],
              scheduledEnabled: sequenceData.scheduledEnabled ?? false,
              scheduledDays: sequenceData.scheduledDays || [],
              scheduledStartTime: sequenceData.scheduledStartTime || null,
              scheduledFrequencyMinutes: sequenceData.scheduledFrequencyMinutes || 60,
              scheduledMaxTimeoutMinutes: sequenceData.scheduledMaxTimeoutMinutes || 15,
              customTests: sequenceData.customTests ?? [],
              steps: mappedSteps
            }
          }
        });
        showSuccess(`Sequence "${sequenceData.name}" updated successfully!`);
        
        // Only close and clear on successful save
        event('sequence_save', { event_category: 'sequences', is_edit: true });
        setSequenceBuilderOpen(false);
        setEditingSequence(null);
        frozenEditingSequenceRef.current = null;
        refetchSequences();
      } else {
        console.log('➕ [FRONTEND] Creating new sequence');
        
        // Map steps for creation
        const mappedSteps = sequenceData.steps.map((step: any, index: number) => {
          console.log(`🔍 [FRONTEND] Processing step ${index}:`, step);
          
          const baseStep = {
            stepNumber: step.stepNumber,
            name: step.name,
            stepType: step.stepType === 'httpRequest' ? 'http_request' : 
                     step.stepType === 'timer' ? 'timer' : 'action',
            analyzeForBias: step.analyzeForBias,
            analysisText: step.analysisText || '',
            variableDefinitions: step.variableDefinitions || [],
            waitSeconds: step.waitSeconds,
            retryOnFailure: step.retryOnFailure,
            skipConditionVariable: step.skipCondition?.variable || '',
            skipConditionOperator: step.skipCondition?.operator || 'equals',
            skipConditionValue: step.skipCondition?.value || '',
            actionBlocks: JSON.stringify(step.actionBlocks || []),
            // RAG Analysis Configuration
            ragAnalysisEnabled: step.ragAnalysisEnabled || false,
            ragQueryVariable: step.ragQueryVariable || '',
            ragContextVariable: step.ragContextVariable || '',
            ragResponseVariable: step.ragResponseVariable || '',
            // File Attachments
            fileAttachments: step.fileAttachments || [],
          };

          // Only add HTTP-specific fields for HTTP request steps
          if (step.stepType === 'httpRequest') {
            return {
              ...baseStep,
              url: step.url,
              httpMethod: step.method,
              headers: step.headers,
              requestBody: step.requestBody,
              requestFormat: step.requestBodyType || 'json',
              responseVariableMappings: step.responseMapping,
              analysisTextTemplate: step.analysisTextTemplate,
              analysisIncludeVariables: step.analysisIncludeVariables,
              analysisIncludeResponseData: step.analysisIncludeResponseData,
              analysisIncludeContext: step.analysisIncludeContext
            };
          }

          return baseStep;
        });
        
        // Create new sequence
        await createSequence({
          variables: {
            input: {
              name: sequenceData.name,
              description: sequenceData.description,
              status: sequenceData.status || 'draft',
              executionIntervalMinutes: 60,
              maxRetries: 3,
              timeoutSeconds: 30,
              globalVariables: JSON.stringify(sequenceData.globalVariables),
              analysisVariables: sequenceData.analysisVariables || [],
              ignoredHarmCategories: sequenceData.ignoredHarmCategories || [],
              scheduledEnabled: sequenceData.scheduledEnabled ?? false,
              scheduledDays: sequenceData.scheduledDays || [],
              scheduledStartTime: sequenceData.scheduledStartTime || null,
              scheduledFrequencyMinutes: sequenceData.scheduledFrequencyMinutes || 60,
              scheduledMaxTimeoutMinutes: sequenceData.scheduledMaxTimeoutMinutes || 15,
              customTests: sequenceData.customTests ?? [],
              steps: mappedSteps
            }
          }
        });
        showSuccess(`Sequence "${sequenceData.name}" created successfully!`);
        
        // Only close and clear on successful save
        event('sequence_save', { event_category: 'sequences', is_edit: false });
        setSequenceBuilderOpen(false);
        setEditingSequence(null);
        frozenEditingSequenceRef.current = null;
        refetchSequences();
      }
    } catch (error: any) {
      console.error('Failed to save sequence:', error);
      showError(`Failed to save sequence: ${error.message}`);
      // Don't close the sequence builder or clear editing state on failure
      // This preserves the user's work so they can try again
    }
  };


  console.log('🔍 SEQUENCES PAGE RENDER:', {
    loading: sequencesLoading,
    error: sequencesError,
    data: sequencesData,
    sequencesCount: sequencesData?.sequences?.length
  });

  return (
    <Box sx={{ bgcolor: '#f8faf9', minHeight: '100vh', p: 3 }}>
      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You are viewing sequences in read-only mode. You can explore the interface but cannot make changes.
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box sx={{ flex: 1, mr: 3 }}>
          <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
            API Sequences
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '900px', lineHeight: 1.7, fontSize: '0.95rem' }}>
            An API Sequence is an automated workflow or scripted procedure that systematically interacts with a Large Language Model (LLM) application, executing a series of commands designed to test and analyze the resulting output against a defined set of safety and security criteria, ultimately yielding a composite LLM response risk score.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button
            data-ga-label="Sequences Create New"
            variant="contained"
            startIcon={<Code />}
            onClick={handleCreateNewSequence}
            sx={{
              bgcolor: '#0891b2',
              color: 'white',
              '&:hover': {
                bgcolor: '#4f46e5'
              }
            }}
          >
            Create New
          </Button>
        </Box>
      </Box>

      {/* Active Sequences */}
      {true && (
        <Grid container spacing={3}>
          {sequencesLoading ? (
            <Grid item xs={12}>
              <Card sx={{ p: 3 }}>
                <LinearProgress />
                <Typography sx={{ mt: 2 }}>Loading sequences...</Typography>
              </Card>
            </Grid>
          ) : sequencesError ? (
            <Grid item xs={12}>
              <Alert severity="error">
                Failed to load sequences: {sequencesError ? JSON.stringify(sequencesError) : 'Unknown error'}
              </Alert>
            </Grid>
          ) : !sequencesData?.sequences || sequencesData.sequences.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info">
                No sequences found. Create your first sequence to get started.
                <br />
                <strong>Debug:</strong> sequencesData = {JSON.stringify(sequencesData)}
                <br />
                <strong>Loading:</strong> {sequencesLoading.toString()}
                <br />
                <strong>Error:</strong> {sequencesError ? JSON.stringify(sequencesError) : 'None'}
              </Alert>
            </Grid>
          ) : (
            sequencesData.sequences.map((sequence: Sequence) => (
              <Grid item xs={12} md={6} key={sequence.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1f2937' }}>
                        {sequence.name}
                      </Typography>
                      <Chip
                        label={sequence.status}
                        color={getStatusColor(sequence.status) as any}
                        size="small"
                        sx={{ 
                          fontWeight: 600,
                          ...(sequence.status === 'active' && {
                            bgcolor: '#1e3a5f',
                            color: 'white',
                            '& .MuiChip-label': {
                              color: 'white'
                            }
                          })
                        }}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                      {sequence.description}
                    </Typography>

                    <Box 
                      sx={{ 
                        mb: 3, 
                        p: 1.5, 
                        bgcolor: '#f8faf9', 
                        borderRadius: 1,
                        borderLeft: '3px solid #6b7280'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Last Successful Execution
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                        {sequence.lastExecuted ? new Date(sequence.lastExecuted).toLocaleString() : 'Never'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', pt: 2, borderTop: '1px solid #e5e7eb' }}>
                      <Button
                        data-ga-label="Sequence Execute"
                        variant="contained"
                        startIcon={runningSequences.has(sequence.id) ? <Stop /> : <PlayArrow />}
                        onClick={() => handleExecuteSequence(sequence)}
                        disabled={!canEdit || runningSequences.has(sequence.id) || sequence.status !== 'active'}
                        sx={{
                          bgcolor: '#1e3a5f',
                          color: 'white',
                          fontWeight: 600,
                          px: 3,
                          '&:hover': {
                            bgcolor: '#059669'
                          },
                          '&:disabled': {
                            bgcolor: '#9ca3af',
                            color: 'white'
                          }
                        }}
                      >
                        {runningSequences.has(sequence.id) ? 'Running...' : 'Execute'}
                      </Button>
                      <Button
                        data-ga-label="Sequence View"
                        variant="outlined"
                        startIcon={<Code />}
                        onClick={() => {
                          setSelectedSequence(sequence);
                          logEvent('sequence', 'view', `Viewed sequence '${sequence.name}'`, {
                            targetType: 'Sequence',
                            targetId: sequence.id,
                            targetName: sequence.name,
                          });
                        }}
                        sx={{
                          borderColor: '#6b7280',
                          color: '#6b7280',
                          fontWeight: 600,
                          '&:hover': {
                            borderColor: '#4b5563',
                            bgcolor: 'rgba(107, 114, 128, 0.08)'
                          }
                        }}
                      >
                        View
                      </Button>
                      <Button
                        data-ga-label="Sequence Edit"
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => handleEditSequence(sequence)}
                        sx={{
                          borderColor: '#6b7280',
                          color: '#6b7280',
                          fontWeight: 600,
                          '&:hover': {
                            borderColor: '#4b5563',
                            bgcolor: 'rgba(107, 114, 128, 0.08)'
                          }
                        }}
                      >
                        Edit
                      </Button>
                      <IconButton
                        data-ga-label="Sequence Delete"
                        color="error"
                        onClick={() => handleDeleteSequence(sequence)}
                        disabled={!canEdit}
                        sx={{
                          '&:hover': {
                            bgcolor: 'rgba(220, 38, 38, 0.08)'
                          }
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}


      {/* Templates functionality removed - not implemented in backend */}

      {/* Sequence Details Dialog */}
      {selectedSequence && (
        <Dialog open={Boolean(selectedSequence)} onClose={() => setSelectedSequence(null)} maxWidth="lg" fullWidth>
          <DialogTitle>
            {selectedSequence.name} - Sequence Steps
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {selectedSequence.description}
            </Typography>
            
            {selectedSequence.steps.map((step, index) => (
              <Accordion key={step.id}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Chip label={step.stepNumber} size="small" />
                    <Typography sx={{ fontWeight: 'bold' }}>{step.name}</Typography>
                    <Chip label={step.stepType} size="small" variant="outlined" />
                    {step.analyzeForBias && (
                      <Chip
                        icon={<Assessment />}
                        label="Bias Analysis"
                        size="small"
                        color="primary"
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" gutterBottom>URL:</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                        {step.httpMethod} {step.url}
                      </Typography>
                    </Grid>
                    {step.analysisText && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Analysis Text:</Typography>
                        <Typography variant="body2" sx={{ 
                          fontFamily: 'monospace', 
                          backgroundColor: 'grey.100', 
                          p: 1, 
                          borderRadius: 1,
                          whiteSpace: 'pre-wrap'
                        }}>
                          {step.analysisText}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </DialogContent>
          <DialogActions>
            <Button data-ga-label="Sequence details Close" onClick={() => setSelectedSequence(null)}>Close</Button>
            <Button
              data-ga-label="Sequence details Execute"
              variant="contained"
              startIcon={<PlayArrow />}
              onClick={() => {
                handleExecuteSequence(selectedSequence);
                setSelectedSequence(null);
              }}
              disabled={!canEdit || runningSequences.has(selectedSequence.id)}
            >
              {runningSequences.has(selectedSequence.id) ? 'Running...' : 'Execute Sequence'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Sequence Builder Dialog - Uses frozen ref to prevent re-renders from resetting form */}
      <Dialog 
        open={sequenceBuilderOpen} 
        onClose={handleCloseSequenceBuilder} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            borderRadius: 3,
            border: '1px solid #e2e8f0',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(90deg, #1e3a5f 0%, #0284c7 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.25rem',
        }}>
          {frozenEditingSequenceRef.current ? `Edit Sequence: ${frozenEditingSequenceRef.current.name}` : 'Create New Sequence'}
          {!canEdit && ' (View only)'}
        </DialogTitle>
        <DialogContent sx={{ height: '80vh', bgcolor: '#f8fafc' }}>
          <SequenceBuilder
            key={frozenEditingSequenceRef.current?.id || 'new'}
            onSave={handleSaveSequence}
            readOnly={!canEdit}
            initialData={frozenEditingSequenceRef.current ? {
              id: frozenEditingSequenceRef.current.id,
              name: frozenEditingSequenceRef.current.name,
              description: frozenEditingSequenceRef.current.description,
              status: frozenEditingSequenceRef.current.status,
              globalVariables: typeof frozenEditingSequenceRef.current.globalVariables === 'string' 
                ? JSON.parse(frozenEditingSequenceRef.current.globalVariables) 
                : frozenEditingSequenceRef.current.globalVariables || {},
              ignoredHarmCategories: frozenEditingSequenceRef.current.ignoredHarmCategories || [],
              ignoredGraniteCategories: frozenEditingSequenceRef.current.ignoredGraniteCategories || [],
              llamaGuardEnabled: frozenEditingSequenceRef.current.llamaGuardEnabled ?? true,
              graniteGuardianEnabled: frozenEditingSequenceRef.current.graniteGuardianEnabled ?? true,
              semanticAnalysisEnabled: frozenEditingSequenceRef.current.semanticAnalysisEnabled ?? true,
              ignoredSemanticCategories: frozenEditingSequenceRef.current.ignoredSemanticCategories || [],
              customTests: (frozenEditingSequenceRef.current as Sequence).customTests || [],
              files: (frozenEditingSequenceRef.current as any).files || [],
              scheduledEnabled: frozenEditingSequenceRef.current.scheduledEnabled ?? false,
              scheduledDays: frozenEditingSequenceRef.current.scheduledDays || [],
              scheduledStartTime: frozenEditingSequenceRef.current.scheduledStartTime || '09:00',
              scheduledFrequencyMinutes: frozenEditingSequenceRef.current.scheduledFrequencyMinutes || 60,
              scheduledMaxTimeoutMinutes: frozenEditingSequenceRef.current.scheduledMaxTimeoutMinutes || 15,
              steps: frozenEditingSequenceRef.current.steps?.map(step => ({
                id: step.id,
                stepNumber: step.stepNumber,
                name: step.name,
                stepType: step.stepType === 'http_request' ? 'httpRequest' : 
                         step.stepType === 'wait' ? 'timer' : 
                         ['condition', 'variable_assignment', 'loop'].includes(step.stepType) ? 'action' : 'action',
                url: step.url || '',
                method: step.httpMethod || 'GET',
                headers: typeof (step as any).headers === 'object' 
                  ? JSON.stringify((step as any).headers || {}, null, 2)
                  : (step as any).headers || '{}',
                requestBody: (step as any).requestBody || '',
                requestBodyType: (step as any).requestFormat || 'json',
                responseMapping: typeof (step as any).responseVariableMappings === 'object'
                  ? JSON.stringify((step as any).responseVariableMappings || {}, null, 2)
                  : (step as any).responseVariableMappings || '{}',
                analyzeForBias: step.analyzeForBias || false,
                analysisText: (step as any).analysisText || '',
                variableDefinitions: (step as any).variableDefinitions || [],
                analysisTextTemplate: (step as any).analysisTextTemplate || '',
                analysisIncludeVariables: (step as any).analysisIncludeVariables || [],
                analysisIncludeResponseData: (step as any).analysisIncludeResponseData !== false,
                analysisIncludeContext: (step as any).analysisIncludeContext !== false,
                waitSeconds: (step as any).waitSeconds || 0,
                retryOnFailure: (step as any).retryOnFailure || false,
                maxRetries: (step as any).maxRetries || 3,
                retryDelay: (step as any).retryDelay || 5,
                skipCondition: {
                  variable: step.skipConditionVariable || '',
                  operator: step.skipConditionOperator || 'equals',
                  value: step.skipConditionValue || ''
                },
                actionBlocks: step.actionBlocks || [],
                // RAG Analysis Configuration
                ragAnalysisEnabled: (step as any).ragAnalysisEnabled || false,
                ragQueryVariable: (step as any).ragQueryVariable || '',
                ragContextVariable: (step as any).ragContextVariable || '',
                ragResponseVariable: (step as any).ragResponseVariable || '',
                // File Attachments
                fileAttachments: (step as any).fileAttachments || [],
              })) || []
            } : undefined}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#f1f5f9', borderTop: '1px solid #e2e8f0', py: 2, px: 3 }}>
          <Button 
            data-ga-label="Sequence builder Cancel"
            onClick={handleCloseSequenceBuilder}
            sx={{ 
              color: '#64748b',
              '&:hover': { bgcolor: 'rgba(100, 116, 139, 0.1)' }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Sequence</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{sequenceToDelete?.name}"? This action cannot be undone.
          </Typography>
          {sequenceToDelete && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This sequence may be assigned to API configurations. Deleting it will remove it from those configurations.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-ga-label="Delete sequence Cancel" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button data-ga-label="Delete sequence Confirm" onClick={confirmDeleteSequence} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create from Template Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Sequence from Template</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2">{selectedTemplate.name}</Typography>
                <Typography variant="body2">{selectedTemplate.description}</Typography>
              </Alert>
              
              <TextField
                fullWidth
                label="Sequence Name"
                value={newSequenceName}
                onChange={(e) => setNewSequenceName(e.target.value)}
                sx={{ mb: 3 }}
              />
              
              <Typography variant="subtitle2" gutterBottom>
                Required Variables (JSON format):
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Required: {selectedTemplate.requiredVariables.join(', ')}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={8}
                label="Variables JSON"
                value={templateVariables}
                onChange={(e) => setTemplateVariables(e.target.value)}
                placeholder={`{
  "${selectedTemplate.requiredVariables[0] || 'variable'}": "value",
  "${selectedTemplate.requiredVariables[1] || 'variable2'}": "value2"
}`}
                sx={{ fontFamily: 'monospace' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-ga-label="Template Cancel" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button
            data-ga-label="Template Create Sequence"
            variant="contained"
            onClick={handleCreateFromTemplate}
            disabled={!newSequenceName || !selectedTemplate}
          >
            Create Sequence
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Modal */}
      <AlertModal
        open={alertState.open}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        confirmText={alertState.confirmText}
        showCancel={alertState.showCancel}
        onConfirm={alertState.onConfirm}
        onCancel={alertState.onCancel}
      />
    </Box>
  );
};

export default Sequences;
